import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useAmazonProfile } from '../contexts/AmazonProfileContext';
import { useTheme } from '../contexts/ThemeContext';
import { roleLabel, userTypeLabel } from '../constants/userRoles';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function marketplaceLabel(profile) {
  if (!profile) return '—';
  const cc = profile.countryCode || profile.region || '—';
  const cur = profile.currencyCode || profile.currency || '—';
  return `${cc} · ${cur}`;
}

export default function UserAccountMenu({ onLogout }) {
  const { user } = useAuth();
  const { selectedProfile } = useAmazonProfile();
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 320 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 16);
    let left = rect.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setMenuStyle({
      top: rect.bottom + 6,
      left,
      width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const permissions = user?.permissions || [];
  const permPreview = permissions.includes('all')
    ? ['Full access']
    : permissions.slice(0, 8);
  const extraPerms = permissions.includes('all') ? 0 : Math.max(0, permissions.length - 8);

  const displayName = user?.name
    || [user?.firstName, user?.lastName].filter(Boolean).join(' ')
    || user?.username
    || 'Account';

  const menuPanel = open ? (
    <div
      ref={menuRef}
      role="menu"
      className={classNames(
        'fixed z-[500] max-h-[min(32rem,80vh)] overflow-y-auto rounded-xl shadow-lg py-1 ring-1',
        isDarkMode ? 'bg-slate-800 ring-slate-700' : 'bg-white ring-black/5'
      )}
      style={{
        top: menuStyle.top,
        left: menuStyle.left,
        width: menuStyle.width,
      }}
    >
      <div
        className={classNames(
          'px-4 py-3 border-b space-y-1',
          isDarkMode ? 'border-slate-700' : 'border-slate-200'
        )}
      >
        <p className={classNames('text-sm font-semibold truncate', isDarkMode ? 'text-slate-100' : 'text-slate-900')}>
          {displayName}
        </p>
        <p className={classNames('text-xs truncate', isDarkMode ? 'text-slate-400' : 'text-slate-500')}>
          {user?.email}
        </p>
        <div className="flex flex-wrap gap-1.5 pt-1">
          <span className="text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-200">
            {roleLabel(user?.role)}
          </span>
          {user?.userType && (
            <span className="text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {userTypeLabel(user.userType)}
            </span>
          )}
        </div>
      </div>

      <div className={classNames('px-4 py-2.5 border-b text-xs space-y-1', isDarkMode ? 'border-slate-700 text-slate-300' : 'border-slate-100 text-slate-600')}>
        <p className="font-medium text-slate-500 dark:text-slate-400">Active store</p>
        <p className={classNames('font-medium truncate', isDarkMode ? 'text-slate-200' : 'text-slate-800')}>
          {selectedProfile?.accountInfo?.name || selectedProfile?.name || 'No store selected'}
        </p>
        <p>{marketplaceLabel(selectedProfile)}</p>
      </div>

      {permPreview.length > 0 && (
        <div className={classNames('px-4 py-2.5 border-b', isDarkMode ? 'border-slate-700' : 'border-slate-100')}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            Permissions
          </p>
          <div className="flex flex-wrap gap-1">
            {permPreview.map((p) => (
              <span
                key={p}
                className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
              >
                {p.replace(/_/g, ' ')}
              </span>
            ))}
            {extraPerms > 0 && (
              <span className="text-[10px] text-slate-500">+{extraPerms} more</span>
            )}
          </div>
        </div>
      )}

      <p className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-700">
        Change password under{' '}
        <span className="font-medium text-slate-600 dark:text-slate-300">Stores → Administration → Account</span>.
      </p>

      <button
        type="button"
        role="menuitem"
        onClick={() => {
          setOpen(false);
          onLogout();
        }}
        className={classNames(
          'flex items-center w-full px-4 py-2 text-sm text-left',
          isDarkMode
            ? 'text-slate-200 hover:bg-slate-700'
            : 'text-slate-700 hover:bg-slate-100'
        )}
      >
        <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4 shrink-0" />
        Sign out
      </button>
    </div>
  ) : null;

  return (
    <>
      <div className="relative shrink-0" ref={triggerRef}>
        <button
          type="button"
          className={classNames(
            'flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500',
            isDarkMode ? 'bg-slate-800 ring-1 ring-slate-700' : 'bg-white ring-1 ring-slate-200',
            open && 'ring-2 ring-orange-500 ring-offset-1'
          )}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <span className="sr-only">Open account menu</span>
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-500 to-stone-900 flex items-center justify-center">
            <UserIcon className="h-4 w-4 text-white" />
          </div>
        </button>
      </div>

      {typeof document !== 'undefined' && menuPanel
        ? createPortal(menuPanel, document.body)
        : null}
    </>
  );
}
